/**
 * questionConfig.js
 * 
 * A cleaner, more maintainable approach for:
 *   - Generating question configurations
 *   - Handling Contentful question objects
 *   - Building both normal (wrapped) and inline (for All Questions) components
 */

import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Col, Row } from 'antd';
import Button from '@mui/material/Button';
import { fetchContentGroupByName } from '../../../api/contentGroups';

// ---- Import actual question components here ----
import AllocationSlider from "./elements/AllocationSlider";
import { DateEntry, TextEntry } from "./elements/Basic"; // If needed
import YesNo from "./elements/YesNo";
import Lean from "./elements/Lean";
import CascadeSlider from "./elements/CascadeSlider";
import QuestionWrapper from './QuestionWrapper';
import GridSelector from "./components/GridSelector";
import InfoEntry from "./components/InfoEntry";
import MaxExposureSelector from "./components/MaxExposureSelector";

// -----------------------------------------------------------------------------
// 1) MAPPING from "type" to the actual question component
// -----------------------------------------------------------------------------
const COMPONENT_TYPES = {
  yesNo: YesNo,
  allocation: AllocationSlider,
  lean: Lean,
  slider: CascadeSlider,
  info: InfoEntry,
};

// -----------------------------------------------------------------------------
// 2) HELPER FUNCTIONS to build normal ("wrapped") vs. inline components
// -----------------------------------------------------------------------------
/**
 * Creates a normal step-based question component that:
 *  - Wraps in QuestionWrapper
 *  - Supports next/prev via setCurrentStep
 */
function createWrappedQuestionComponent({
  QuestionComponent,
  questionTitle,
  description,
  contentId,
  currentStep,
  setCurrentStep,
  hideSkip,
}) {
  // Return a React component
  return function WrappedQuestion({ value, onChange }) {
    return (
      <QuestionWrapper
        title={questionTitle}
        description={description}
        infoContentId={contentId}
        currentStep={currentStep}
        setCurrentStep={setCurrentStep}
        hideSkip={hideSkip}
      >
        <div className="flex-1 flex items-center">
          <QuestionComponent
            value={value}
            onChange={onChange}
            question=""
            onNext={() => setCurrentStep(currentStep + 1)}
          />
        </div>
      </QuestionWrapper>
    );
  };
}

/**
 * Creates a minimal "inline" question component for final All-Questions step.
 *  - No QuestionWrapper, no step navigation
 */
function createInlineQuestionComponent(QuestionComponent) {
  return function InlineQuestion({ value, onChange }) {
    return (
      <QuestionComponent
        value={value}
        onChange={onChange}
        question=""
        onNext={() => {}}
        inlineView={true}
      />
    );
  };
}

// -----------------------------------------------------------------------------
// 3) MAIN QUESTION CONFIG GENERATOR
// -----------------------------------------------------------------------------
export function generateQuestionConfig(
  contentfulQuestion,
  currentStep,
  setCurrentStep,
  handleFinish // used in the final question, if needed
) {
  const { name, type, stepTitle, questionTitle, description, contentId } =
    contentfulQuestion.fields;

  // Find the matching question UI component
  const QuestionComponent = COMPONENT_TYPES[type] || null;

  // If no matching component, produce a no-op config to avoid errors
  if (!QuestionComponent) {
    console.warn(`No component found for type: ${type}`);
    return {
      type,
      title: stepTitle || '',
      description: description || '',
      component: () => null,
      componentForAllQuestions: () => null,
    };
  }

  // For "info" type, we might hide the skip button
  const hideSkip = (type === 'info');

  return {
    type,
    title: stepTitle,
    description,
    // Normal step-based usage
    component: createWrappedQuestionComponent({
      QuestionComponent,
      questionTitle,
      description,
      contentId,
      currentStep,
      setCurrentStep,
      hideSkip,
    }),
    // Minimal "inline" usage for final All-Questions
    componentForAllQuestions: createInlineQuestionComponent(QuestionComponent),
  };
}

// -----------------------------------------------------------------------------
// 4) HOOK: useQuestionConfiguration
//    - Fetch the group data
//    - Build question/step configs in the right order
// -----------------------------------------------------------------------------
export function useQuestionConfiguration(
  groupName = 'portfolioPathQuestions',
  currentStep,
  setCurrentStep,
  handleFinish
) {
  const dispatch = useDispatch();
  const questionsData = useSelector(
    (state) => state.contentGroups.groups[groupName]
  );
  const status = useSelector((state) => state.contentGroups.status[groupName]);
  const questionIds = useSelector((state) => state.path.questionIds);

  // Fetch from Contentful if not already loaded
  useEffect(() => {
    if (!questionsData && status !== 'loading') {
      dispatch(fetchContentGroupByName(groupName));
    }
  }, [dispatch, groupName, questionsData, status]);

  /**
   * generatePathQuestions
   *  - Returns [questions, steps] for the stepper
   */
  const generatePathQuestions = ({ customPathPages = [] }) => {
    // If nothing loaded yet, return empty
    if (!questionsData) {
      return [{}, []];
    }

    // Filter out only the relevant questions (based on questionIds in Redux)
    const filteredQuestions = questionsData.filter((question) =>
      questionIds.includes(question.sys.id)
    );

    // Sort them by the order in questionIds
    const sortedQuestions = [...filteredQuestions].sort((a, b) => {
      return questionIds.indexOf(a.sys.id) - questionIds.indexOf(b.sys.id);
    });

    // Build the question config objects
    const questions = sortedQuestions.reduce((acc, question) => {
      const config = generateQuestionConfig(
        question,
        currentStep,
        setCurrentStep,
        handleFinish
      );
      acc[question.fields.name] = config;
      return acc;
    }, {});

    // Build the steps list (for the Stepper)
    const steps = sortedQuestions.map((question) => ({
      id: question.fields.name,
      title: question.fields.stepTitle,
    }));

    // -------------------------------------------------------------------------
    // Handle custom path pages (if provided)
    // -------------------------------------------------------------------------
    customPathPages.forEach((page, index) => {
      const customPageKey = `customPage${index}`;
      questions[customPageKey] = {
        title: page.title,
        // For custom pages, we can wrap them in QuestionWrapper or not
        component: ({ value, onChange }) => (
          <QuestionWrapper
            title={page.title}
            description={page.description}
            currentStep={currentStep}
            setCurrentStep={setCurrentStep}
          >
            <GridSelector
              value={value}
              onChange={onChange}
              portfolioIds={page.portfolio_ids}
              maxSelect={page.max_select}
              description={page?.description}
            />
          </QuestionWrapper>
        ),
        // Optionally define componentForAllQuestions if you want
        componentForAllQuestions: ({ value, onChange }) => (
          <GridSelector
            value={value}
            onChange={onChange}
            portfolioIds={page.portfolio_ids}
            maxSelect={page.max_select}
            description={page?.description}
          />
        ),
      };
      steps.push({ id: customPageKey, title: page.title });
    });

    // -------------------------------------------------------------------------
    // Append a final "maxExposures" step as per your code
    // -------------------------------------------------------------------------
    questions["maxExposures"] = {
      title: "Max Exposures",
      component: ({ value, onChange }) => (
        <QuestionWrapper
          title="Fine Tune Exposures"
          description="You can optionally assign a maximum exposure % allowed for each special asset class."
          currentStep={currentStep}
          setCurrentStep={setCurrentStep}
        >
          <Row justify="center">
            <Button
              onClick={handleFinish}
              size="large"
              variant="contained"
              color="info"
            >
              Finish and Continue
            </Button>
          </Row>
          <MaxExposureSelector value={value} onChange={onChange} />
        </QuestionWrapper>
      ),
      componentForAllQuestions: ({ value, onChange }) => (
        <MaxExposureSelector value={value} onChange={onChange} />
      ),
    };
    steps.push({ id: "maxExposures", title: "Max Exposures" });

    // Return the final object
    return [questions, steps];
  };

  // Return the hook's public API
  return {
    generatePathQuestions,
    isLoading: status === 'loading',
    error: status === 'failed',
  };
}
